<template>
  <div
    class="flow-component absolute right-4 z-50 flex select-none flex-col gap-2"
    :class="[
      route.meta?.fullScreen ? 'top-4' : 'top-4',
      templatePreview ? 'top-4' : ''
    ]"
  >
    <!-- Zoom Controls Group -->
    <div class="flex flex-col overflow-hidden rounded bg-white shadow ring-1 ring-surface-900/10 dark:bg-slate-900 dark:ring-surface-200/20">
      <button
        v-tooltip.left="'Zoom in'"
        :class="[
          'flex items-center justify-center p-2.5 transition-all',
          canZoomIn
            ? 'group flex min-w-[42px] cursor-pointer flex-col items-center justify-center gap-1 rounded bg-white p-2.5 shadow ring-1 ring-surface-900/10 transition-all hover:bg-gray-100 dark:bg-slate-900 dark:ring-surface-200/20 dark:hover:bg-slate-900'
            : 'cursor-not-allowed text-gray-300 dark:text-gray-600'
        ]"
        @click="canZoomIn ? emits('zoomIn') : null"
      >
        <iconify-icon
          icon="heroicons:plus"
          height="18"
          width="18"
          class="pointer-events-none text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200"
        />
      </button>
      <div class="border-t border-gray-200/60 dark:border-gray-700/60" />
      <button
        v-tooltip.left="'Zoom out'"
        :class="[
          'flex items-center justify-center p-2.5 transition-all',
          canZoomOut
            ? 'group flex min-w-[42px] cursor-pointer flex-col items-center justify-center gap-1 rounded bg-white p-2.5 shadow ring-1 ring-surface-900/10 transition-all hover:bg-gray-100 dark:bg-slate-900 dark:ring-surface-200/20 dark:hover:bg-slate-900'
            : 'cursor-not-allowed text-gray-300 dark:text-gray-600'
        ]"
        @click="canZoomOut ? emits('zoomOut') : null"
      >
        <iconify-icon
          icon="heroicons:minus"
          height="18"
          width="18"
          class="pointer-events-none text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200"
        />
      </button>
      <div class="border-t border-gray-200/60 dark:border-gray-700/60" />
      <button
        v-tooltip.left="'Fit to screen'"
        class="group flex min-w-[42px] cursor-pointer flex-col items-center justify-center gap-1 rounded bg-white p-2.5 shadow ring-1 ring-surface-900/10 transition-all hover:bg-gray-100 dark:bg-slate-900 dark:ring-surface-200/20 dark:hover:bg-slate-900"
        @click="emits('center')"
      >
        <iconify-icon
          icon="heroicons:arrows-pointing-out"
          height="18"
          width="18"
          class="pointer-events-none text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200"
        />
      </button>
    </div>

    <!-- Zoom Percentage (separate pill) -->
    <div class="flex items-center justify-center rounded bg-white px-3 py-1.5 text-xs font-medium text-gray-500 shadow ring-1 ring-surface-900/10 dark:bg-slate-900 dark:text-gray-400 dark:ring-surface-200/20">
      {{ scalePercentange(scale) }}
    </div>

    <!-- Visitor Count (if metrics enabled) -->
    <div
      v-if="!templatePreview && showHeatMapToggle"
      v-tooltip.left="'View engagement data'"
      class="group flex min-w-[42px] cursor-pointer flex-col items-center justify-center gap-1 rounded bg-white p-2.5 shadow ring-1 ring-surface-900/10 transition-all hover:bg-gray-100 dark:bg-slate-900 dark:ring-surface-200/20 dark:hover:bg-slate-900"
      @click="showEngagementDrawer = true"
    >
      <iconify-icon
        icon="heroicons:user-group"
        height="18"
        width="18"
        class="pointer-events-none text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200"
      />
      <Skeleton
        v-if="isLoadingMetrics"
        width="2rem"
        height="0.875rem"
        class="!bg-gray-200 dark:!bg-gray-700"
      />
      <span
        v-else
        class="min-w-[2rem] text-center text-xs font-medium text-gray-600 dark:text-gray-300"
      >
        {{ formatCompactNumber(visitorCount) }}
      </span>
    </div>

    <!-- A/B Test Button (shown when no active test and feature is unlocked) -->
    <button
      v-if="!templatePreview && !isSystemFlow && showAbTestButton"
      v-tooltip.left="'A/B test this flow'"
      class="group flex min-w-[42px] cursor-pointer flex-col items-center justify-center gap-1 rounded bg-white p-2.5 shadow ring-1 ring-surface-900/10 transition-all hover:bg-gray-100 dark:bg-slate-900 dark:ring-surface-200/20 dark:hover:bg-slate-900"
      @click="emits('createFlowTest')"
    >
      <iconify-icon
        icon="heroicons:beaker"
        height="18"
        width="18"
        class="pointer-events-none text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200"
      />
    </button>

    <!-- Actions Group (Preview, Copy) -->
    <div
      v-if="!templatePreview && (!isSystemFlow || canCopyFlow)"
      class="flex flex-col overflow-hidden rounded bg-white shadow ring-1 ring-surface-900/10 dark:bg-slate-900 dark:ring-surface-200/20"
    >
      <button
        v-if="!isSystemFlow"
        v-tooltip.left="'Preview flow'"
        class="group flex min-w-[42px] cursor-pointer flex-col items-center justify-center gap-1 rounded rounded-b-none bg-white p-2.5 shadow ring-1 ring-surface-900/10 transition-all hover:bg-gray-100 dark:bg-slate-900 dark:ring-surface-200/20 dark:hover:bg-slate-900"
        @click="emits('preview')"
      >
        <iconify-icon
          icon="heroicons:eye"
          height="18"
          width="18"
          class="pointer-events-none text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200"
        />
      </button>
      <div
        v-if="!isSystemFlow && canCopyFlow"
        class="border-t border-gray-200/60 dark:border-gray-700/60"
      />
      <button
        v-if="canCopyFlow"
        v-tooltip.left="'Copy flow'"
        class="group flex min-w-[42px] cursor-pointer flex-col items-center justify-center gap-1 rounded bg-white p-2.5 shadow ring-1 ring-surface-900/10 transition-all hover:bg-gray-100 dark:bg-slate-900 dark:ring-surface-200/20 dark:hover:bg-slate-900"
        @click="emits('copy')"
      >
        <iconify-icon
          icon="heroicons:clipboard-document"
          height="18"
          width="18"
          class="pointer-events-none text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200"
        />
      </button>
      <div
        v-if="canCopyFlow && isAdmin"
        class="border-t border-gray-200/60 dark:border-gray-700/60"
      />
      <button
        v-if="isAdmin"
        v-tooltip.left="'Copy flow JSON'"
        class="group flex min-w-[42px] cursor-pointer flex-col items-center justify-center gap-1 rounded bg-white p-2.5 shadow ring-1 ring-surface-900/10 transition-all hover:bg-gray-100 dark:bg-slate-900 dark:ring-surface-200/20 dark:hover:bg-slate-900"
        @click="copyFlowJson"
      >
        <iconify-icon
          icon="heroicons:code-bracket"
          height="18"
          width="18"
          class="pointer-events-none text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200"
        />
      </button>
    </div>

    <!-- Undo/Redo Group -->
    <div
      v-if="!templatePreview"
      class="flex flex-col overflow-hidden rounded bg-white shadow ring-1 ring-surface-900/10 dark:bg-slate-900 dark:ring-surface-200/20"
    >
      <button
        v-tooltip.left="'Undo'"
        :class="[
          'flex items-center justify-center p-2.5 transition-all',
          canUndo
            ? 'group flex min-w-[42px] cursor-pointer flex-col items-center justify-center gap-1 rounded bg-white p-2.5 shadow ring-1 ring-surface-900/10 transition-all hover:bg-gray-100 dark:bg-slate-900 dark:ring-surface-200/20 dark:hover:bg-slate-900'
            : 'cursor-not-allowed text-gray-300 dark:text-gray-600'
        ]"
        @click="canUndo ? emits('undo') : null"
      >
        <iconify-icon
          icon="hugeicons:undo-03"
          height="18"
          width="18"
          :class="[canUndo ? 'pointer-events-none text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200' : '']"
        />
      </button>
      <div class="border-t border-gray-200/60 dark:border-gray-700/60" />
      <button
        v-tooltip.left="'Redo'"
        :class="[
          'flex items-center justify-center p-2.5 transition-all',
          canRedo
            ? 'group flex min-w-[42px] cursor-pointer flex-col items-center justify-center gap-1 rounded bg-white p-2.5 shadow ring-1 ring-surface-900/10 transition-all hover:bg-gray-100 dark:bg-slate-900 dark:ring-surface-200/20 dark:hover:bg-slate-900'
            : 'cursor-not-allowed text-gray-300 dark:text-gray-600'
        ]"
        @click="canRedo ? emits('redo') : null"
      >
        <iconify-icon
          icon="hugeicons:redo-03"
          height="18"
          width="18"
          :class="[canRedo ? 'pointer-events-none text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200' : '']"
        />
      </button>
    </div>

    <!-- Keyboard Shortcuts Button -->
    <button
      v-if="!templatePreview"
      v-tooltip.left="'Keyboard shortcuts'"
      class="group flex min-w-[42px] cursor-pointer flex-col items-center justify-center gap-1 rounded bg-white p-2.5 shadow ring-1 ring-surface-900/10 transition-all hover:bg-gray-100 dark:bg-slate-900 dark:ring-surface-200/20 dark:hover:bg-slate-900"
      @click="showKeyboardShortcuts = true"
    >
      <iconify-icon
        icon="heroicons:map"
        height="18"
        width="18"
        class="pointer-events-none text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200"
      />
    </button>

    <PreviewModal
      :visible="showPreviewModal"
      @update:visible="showPreviewModal = $event"
    />

    <KeyboardShortcutsModal
      :visible="showKeyboardShortcuts"
      @close="showKeyboardShortcuts = false"
    />

    <FlowEngagementDrawer
      v-model:visible="showEngagementDrawer"
      :flow-id="(route.params.offerFunnelId as string)"
    />
  </div>
</template>

<script setup lang="ts">
import KeyboardShortcutsModal from './KeyboardShortcutsModal.vue'
import FlowEngagementDrawer from './FlowEngagementDrawer.vue'

const route = useRoute()

const showPreviewModal = ref(false)
const showKeyboardShortcuts = ref(false)
const showEngagementDrawer = ref(false)
const templatePreview = inject('templatePreview', false)

// Show keyboard shortcuts on '?' key
onKeyStroke('?', (e) => {
  // Skip if user is typing in an input
  const target = e.target as HTMLElement
  const tagName = target.tagName.toLowerCase()
  if (tagName === 'input' || tagName === 'textarea' || target.isContentEditable) return

  e.preventDefault()
  showKeyboardShortcuts.value = true
})

const emits = defineEmits(['zoomIn', 'zoomOut', 'center', 'preview', 'copy', 'undo', 'redo', 'createFlowTest'])

const { scale, canZoomIn, canZoomOut, canUndo, canRedo } = defineProps({
  scale: Number,
  canZoomIn: Boolean,
  canZoomOut: Boolean,
  canUndo: Boolean,
  canRedo: Boolean
})

// Get visitor count from flow metrics
const flowMetricsStore = useFlowMetricsStore()
const flowMetricsQuery = computed(() => {
  const flowId = route.params.offerFunnelId
  if (!flowId) return null
  return flowMetricsStore.flowQueries[flowId]
})
const isLoadingMetrics = computed(() => flowMetricsQuery.value?.isLoading ?? true)
const visitorCount = computed(() => {
  const metrics = flowMetricsQuery.value?.data
  return metrics?.flowMetrics?.uniqueVisitors || 0
})

// Format large numbers compactly (e.g., 1.2k, 10k, 1.5M)
const formatCompactNumber = (num: number): string => {
  if (num < 1000) return num.toString()
  if (num < 10000) return `${(num / 1000).toFixed(1).replace(/\.0$/, '')}k`
  if (num < 1000000) return `${Math.round(num / 1000)}k`
  return `${(num / 1000000).toFixed(1).replace(/\.0$/, '')}M`
}

const scalePercentange = (scale) => {
  return `${Math.round(scale * 100)}%`
}

const widgetStore = useWidgetStore()

const associatedWidgets = computed(() =>
  widgetStore.getWidgetsForOfferFunnel(route.params.offerFunnelId)
)

const flowStore = useFlowStore()
const currentFlow = computed(() => flowStore.getById(route.params.offerFunnelId))
const isSystemFlow = computed(() => currentFlow.value?.isSystemFlow === true)

const authStore = useAuthStore()
const { isAdmin, canCopyFlow } = storeToRefs(authStore)

const notificationStore = useNotificationStore()

// Copy the flow's funnel definition JSON to clipboard (admin only)
const copyFlowJson = async () => {
  if (!currentFlow.value?.funnelDefinition) return

  try {
    const json = JSON.stringify(currentFlow.value.funnelDefinition, null, 2)
    await navigator.clipboard.writeText(json)
    notificationStore.addNotification('Flow JSON copied to clipboard')
  } catch (error) {
    notificationStore.addError('Failed to copy JSON to clipboard')
  }
}

const settingsStore = useSettingsStore()
const { settings: globalSettings } = storeToRefs(settingsStore)

// Check if enhanced flow reporting is enabled (for showing visitor count)
const showHeatMapToggle = computed(() => {
  return globalSettings.value?.enhancedFlowReporting === true
})

// Show A/B test button when no active/draft test exists and feature is unlocked
const splitTestStore = useSplitTestStore()
const { isFeatureLocked } = useFeatureGating()
const showAbTestButton = computed(() => {
  if (isFeatureLocked('split_testing')) return false
  const flowId = route.params.offerFunnelId as string
  if (!flowId) return false
  return !splitTestStore.getActiveTestForResource('flow', flowId)
})
</script>
